import { z } from 'zod';
import { REQUEST_ITEMS } from './requests.js';

// Onboarding contracts (handover 07 §2 App 1, 03 A-01…A-10, P-01…P-03). Request bodies are strict (unknown fields are
// refused). Money is a string of minor units on the wire. Customer and internal views are SEPARATE types: the
// customer type has no field that could carry a compliance outcome (invariant #10).

const iso2 = z.string().regex(/^[A-Za-z]{2}$/, 'ISO country code');
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');
const minor = z.string().regex(/^\d{1,15}$/, 'amount in minor units');
const text = (max: number) => z.string().trim().min(1).max(max);
export const uuid = z.string().uuid();

export const PartyInput = z.object({
  key: text(40), isApplicant: z.boolean(), kind: z.enum(['INDIVIDUAL', 'ENTITY']), name: text(200), nameLocal: z.string().max(200).nullish(),
  dob: isoDate.nullish(), incorporationDate: isoDate.nullish(), nationality: iso2.nullish(), address: z.string().max(500).nullish(),
  idDocNumber: z.string().max(60).nullish(),
}).strict();

export const RelationshipInput = z.object({
  from: text(40), to: text(40), type: z.enum(['OWNS', 'DIRECTOR_OF', 'SIGNATORY_OF', 'CONTROLS']), ownershipPct: z.number().positive().max(100).nullish(),
}).strict();

export const DraftBody = z.object({
  type: z.enum(['INDIVIDUAL', 'CORPORATE']), legalName: text(200), email: z.string().email().max(320), country: iso2, industry: text(200),
  website: z.string().url().max(300).nullish(), incorporationDate: isoDate.nullish(), brNumber: z.string().max(40).nullish(),
  declared: z.object({
    monthlyVolumeHkdMinor: minor, txPerMonth: z.number().int().min(0).max(100_000), corridors: z.array(iso2).max(30),
    sourceOfFunds: z.string().max(500), purpose: z.string().max(500),
  }).strict(),
  // P-02 step 5 declaration: "I am / am not a politically exposed person" (+ detail of the public role).
  publicRole: z.boolean(), publicRoleDetail: z.string().max(1000).nullish(),
  parties: z.array(PartyInput).min(1).max(50), relationships: z.array(RelationshipInput).max(200),
}).strict();
export type DraftBody = z.infer<typeof DraftBody>;

export const UpdateDraftBody = z.object({ version: z.number().int().min(0), draft: DraftBody }).strict();
export const RfiResponseBody = z.object({ note: text(2000) }).strict();
export const VersionBody = z.object({ version: z.number().int().min(0).optional() }).strict();
export const DisposeHitBody = z.object({
  disposition: z.enum(['TRUE_MATCH', 'FALSE_POSITIVE', 'ESCALATED_MLRO']), reasonCode: text(60), rationale: z.string().max(4000),
  evidenceRef: z.string().max(500).nullish(), version: z.number().int().min(0).optional(),
}).strict();
export const MlroDecisionBody = z.object({ decision: z.enum(['TRUE_MATCH', 'FALSE_POSITIVE']), rationale: z.string().max(4000), version: z.number().int().min(0).optional() }).strict();
export const ChecklistBody = z.object({
  item: z.enum(['IDENTIFY_CUSTOMER', 'VERIFY_CUSTOMER', 'IDENTIFY_UBO', 'VERIFY_UBO', 'OWNERSHIP_STRUCTURE', 'PPTA_IDENTIFIED', 'PURPOSE_OF_RELATIONSHIP', 'SCREENING_ALL_PARTIES', 'NFTF_MEASURE', 'RISK_ASSESSED']),
  evidence: text(2000), nftfMeasure: z.enum(['A_FURTHER_DOCUMENT', 'B_SUPPLEMENTARY_MEASURE', 'C_FIRST_PAYMENT_OWN_ACCOUNT']).optional(),
  version: z.number().int().min(0).optional(),
}).strict();
/** `items`: standard items as worded on the list, plus any the officer wrote; written items need `freeTextConfirmed` (ADR-096). */
export const RfiBody = z.object({ items: z.array(text(300)).min(1).max(20), message: text(2000), dueDate: isoDate.optional(), freeTextConfirmed: z.boolean().optional(),
  version: z.number().int().min(0).optional() }).strict();
export const RejectBody = z.object({ reason: z.string().max(2000), version: z.number().int().min(0).optional() }).strict();
export const AssignBody = z.object({ ids: z.array(uuid).min(1).max(100), assigneeId: uuid.nullable() }).strict();
export const DocumentDecisionBody = z.object({
  decision: z.enum(['ACCEPT', 'REJECT', 'REQUEST_REPLACEMENT']), reason: z.string().max(2000).optional(), message: z.string().max(2000).optional(),
  version: z.number().int().min(0).optional(),
}).strict();
export const AmendBody = z.object({
  field: z.enum(['ADDRESS', 'ID_DOC_NUMBER', 'ID_DOC_PLACE', 'DOB', 'PLACE_OF_BIRTH']), value: z.string().max(500), evidenceRef: text(500), version: z.number().int().min(0),
}).strict();
export const ApplicationListQuery = z.object({
  states: z.string().max(200).optional(), bands: z.string().max(60).optional(), screening: z.enum(['CLEAR', 'HIT_OPEN']).optional(),
  search: z.string().max(100).optional(), assignedTo: uuid.optional(), cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
}).strict();
export const DocumentUploadQuery = z.object({ docType: text(40), fileName: text(200), partyId: uuid.optional() }).strict();

// ───────────── Customer views (applicant app: P-02, P-03) ─────────────

export type CustomerApplicationStatus = 'DRAFT' | 'UNDER_REVIEW' | 'ACTION_REQUIRED' | 'APPROVED' | 'CLOSED';

export interface CustomerApplicationSummary { id: string; reference: string; type: 'INDIVIDUAL' | 'CORPORATE'; legalName: string; status: CustomerApplicationStatus; createdAt: string; submittedAt: string | null }

export interface CustomerParty {
  id: string; isApplicant: boolean; kind: 'INDIVIDUAL' | 'ENTITY'; name: string; nameLocal: string | null; dob: string | null;
  incorporationDate: string | null; nationality: string | null; address?: string | null; idDocNumber?: string | null;
}

export interface CustomerApplicationView {
  id: string; reference: string; type: 'INDIVIDUAL' | 'CORPORATE'; status: CustomerApplicationStatus; version: number;
  legalName: string; email: string; country: string; industry: string; website: string | null; incorporationDate: string | null;
  brNumber?: string | null;                     // drafts only (the applicant's own entry)
  declared: { monthlyVolumeHkdMinor: string; txPerMonth: number; corridors: string[]; sourceOfFunds: string; purpose: string };
  /** The applicant's own declaration only, never the assessed status. */
  publicRole: boolean; publicRoleDetail: string | null;
  parties: CustomerParty[]; relationships: Array<{ id: string; fromPartyId: string; toPartyId: string; type: string; ownershipPct: number | null }>;
  /** Open requests for information only: what is asked, by when. Never why. */
  requests: Array<{ id: string; items: string[]; message: string; dueDate: string; requestedAt: string }>;
  actions: Array<'EDIT_DRAFT' | 'SUBMIT' | 'RESPOND_RFI'>;
  createdAt: string; submittedAt: string | null;
}

export interface CustomerDocumentView { id: string; versionNo: number; docType: string; label: string; fileName: string; partyId: string | null; uploadedAt: string }
/** The request-for-information item that asks for a person's identity check to be done again. Staff pick it in the
 *  request composer; the customer app shows a Start button for each person it names. */
export const IDENTITY_REDO_ITEM = 'New identity check: ';
export const identityRedoItem = (name: string) => `${IDENTITY_REDO_ITEM}${name}`;
/** Each standard request item and the document type it asks the applicant to upload (null: an answer in words, no
 *  document). The customer app offers the upload only for what was asked. The specification's list (ADR-096) comes
 *  first; the earlier fixed items stay so requests already sent keep their upload buttons. */
export const RFI_ITEM_DOCUMENTS: Readonly<Record<string, string | null>> = {
  ...Object.fromEntries(Object.values(REQUEST_ITEMS).map(i => [i.label, i.docType])),
  // Items offered before ADR-096 (requests already sent):
  'Proof of address dated within 3 months': 'PROOF_OF_ADDRESS',
  'Clearer copy of identity document': 'ID_DOCUMENT',
  'Business registration certificate': 'BUSINESS_REGISTRATION',
  'Ownership chart': 'OWNERSHIP_CHART',
  'Source of funds evidence': 'SOURCE_OF_FUNDS',
  'Explanation of expected payment activity': null,
  'Bank statement (last 3 months)': 'BANK_STATEMENT',
};

/** A person's identity check as the applicant sees it (ADR-081): what the verification partner already showed them, never
 *  the reasons. `attemptsLeft`: declined checks they may still retry before being asked to contact us. */
export interface CustomerIdentityCheck {
  partyId: string; name: string; check: 'IDENTITY';
  state: 'NOT_STARTED' | 'IN_PROGRESS' | 'NOT_COMPLETED' | 'SUBMITTED' | 'VERIFIED' | 'NOT_VERIFIED'; startedAt: string | null; attemptsLeft: number;
}

// ───────────── Internal views (admin and console apps) ─────────────

export interface InternalApplicationSummary {
  id: string; reference: string; legalName: string; type: 'INDIVIDUAL' | 'CORPORATE'; country: string; industry: string; state: string;
  riskBand: string | null; submittedAt: string | null; createdAt: string; updatedAt: string; openHits: number; pep: boolean;
  assignedTo: string | null; assignedToName: string | null; decidedAt: string | null;
}

export interface InternalApplicationView {
  application: Record<string, unknown>;          // the full application (money as strings)
  availableActions: string[]; approveExplanation: string | null; beneficialOwnerIds: string[];
  checklistProgress: { done: number; total: number; complete: boolean };
  documentsAttention: string | null;
  /** Possible matches whose list entry is only a PEP record: confirming one does not block (ADR-082). */
  pepOnlyHitIds: string[];
  /** This user may fetch the provider's full details again for older screenings (ADR-084). */
  canRefreshScreening: boolean;
  /** Reason codes for each match outcome (ADR-105). */
  hitReasonCodes: Record<'TRUE_MATCH' | 'FALSE_POSITIVE' | 'ESCALATED_MLRO', string[]>;
}
